import { type FC, useRef, useState } from 'react';

import { isEqual } from 'lodash';
import type { Nullable } from 'types';

import { GridColumn, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { HistoryChips } from 'components/BalanceAndBonuses/History/components/HistoryChips';
import { HistoryRecords } from 'components/BalanceAndBonuses/History/components/HistoryRecords';
import {
    EmptyHistoryCategoryPlaceHolder,
    EmptyHistoryPlaceHolder,
} from 'components/BalanceAndBonuses/History/components/Placeholders';
import HistorySkeleton from 'components/BalanceAndBonuses/History/components/Skeleton';

import {
    BALANCE_HISTORY_CONFIG,
    BalanceHistory,
    BalanceHistoryFilterType,
    selectBalanceHistory,
    selectBalanceHistoryBonus,
    selectBalanceHistoryOperation,
} from 'store/models/balance';
import { useSelector } from 'store/utils';

import styles from 'components/BalanceAndBonuses/History/History.module.less';

const TrlKeys = {
    header: 'BalanceAndBonuses.History.header',
};

const selectors = { selectBalanceHistoryOperation, selectBalanceHistoryBonus };

const splitHistoryRecordsByMonth = (transactions: BalanceHistory): Array<BalanceHistory> =>
    transactions.reduce(
        (result, transaction) => {
            if (BALANCE_HISTORY_CONFIG[transaction.type]) {
                const month = new Date(transaction.dateEvent).getMonth();

                if (result.month !== month) {
                    result.month = month;
                    result.records.push([]);
                }

                result.records.at(-1)?.push(transaction);
            }

            return result;
        },
        { records: [], month: null } as { records: Array<BalanceHistory>; month: Nullable<number> }
    ).records;

const HistoryBase = () => {
    const trl = useTrls();
    const [selectedFilterType, setSelectedFilterType] = useState<Nullable<BalanceHistoryFilterType>>(null);
    const transactions = useSelector(
        selectedFilterType ? selectors[`selectBalanceHistory${selectedFilterType}`] : selectBalanceHistory,
        isEqual
    );
    const onChipClick = useRef((filterType: BalanceHistoryFilterType) =>
        setSelectedFilterType((currentFilterType) => (currentFilterType === filterType ? null : filterType))
    ).current;

    const historyRecords = splitHistoryRecordsByMonth(transactions);

    return (
        <GridColumn xs={4} s={4} m={7} l={6}>
            <div className={styles.wrapper}>
                <Text typography="title-4-semibold">{trl(TrlKeys.header)}</Text>

                {historyRecords.length || selectedFilterType !== null ? (
                    <>
                        <HistoryChips selectedFilterType={selectedFilterType} onChipClick={onChipClick} />

                        {historyRecords.length ? (
                            <HistoryRecords historyRecords={historyRecords} />
                        ) : (
                            <>
                                <VSpacing default={28} />
                                <EmptyHistoryCategoryPlaceHolder />
                            </>
                        )}
                    </>
                ) : (
                    <EmptyHistoryPlaceHolder />
                )}
            </div>
        </GridColumn>
    );
};

export const History: FC<{ loading?: boolean }> = ({ loading }) => {
    return loading ? <HistorySkeleton /> : <HistoryBase />;
};
